import * as THREE from 'three';
import { TugShip } from './TugShip';

/**
 * Lifecycle of a rescue request.
 *
 *   idle      — no rescue active. The radio "Request Rescue" button is
 *               only enabled when this state holds AND fuel = 0.
 *   en-route  — tug is flying from the station to the player at cruise
 *               speed. Visible on the minimap.
 *   arrived   — tug is parked just in front of the player; the FPS
 *               prompt offers the accept-rescue interaction.
 *   returning — accept fired; the camera fades out, the player ship is
 *               teleported back next to the station with a fuel top-up.
 *               This state is brief — once the fade is done we return
 *               to idle.
 */
export type RescueState = 'idle' | 'en-route' | 'arrived' | 'returning';

export interface RescueServiceDependencies {
  /** Live player-ship position provider. */
  readonly getPlayerPosition: () => THREE.Vector3 | null;
  /** Live station position — origin of the rescue tug. */
  readonly getStationPosition: () => THREE.Vector3;
  /** Attach the tug to the active space scene. Receives the full
   *  TugShip so the host can also register it with whatever
   *  per-entity systems care about it (e.g. the planetary orbit
   *  capture so the tug rides along with the planet the player is
   *  drifting near, instead of falling behind). Returns a detach
   *  callback. */
  readonly attachToScene: (tug: TugShip) => () => void;
}

export interface RescueServiceOptions {
  /** Cruise speed of the tug in world units per second. Tuned to read
   *  as "normal patrol speed" — not a warp jump. */
  readonly cruiseSpeed?: number;
  /** Distance in front of the player at which the tug parks. */
  readonly arrivalStandoff?: number;
}

// Tug cruise tuning: bumped 2.3× (was 95) so the recovery ETA is short
// enough to keep the tutorial flowing and so the tug actually catches
// up to a player drifting along with a planet's orbital motion. The
// host adds the tug to the orbit-capture target list so both move
// together when near a planet — combined, the tug always arrives.
const DEFAULT_CRUISE_SPEED = 218;
// Standoff scales with the bigger tug silhouette (≈14u long after the
// 1.75x scale-up). 42u keeps the parked tug fully visible in front of
// the player without crowding them.
const DEFAULT_ARRIVAL_STANDOFF = 42;

/**
 * Owns the rescue lifecycle and the live tug entity. The GameDirector
 * ticks the service every frame (`update(dt)`), and reads `state` /
 * `getTugPosition()` to drive UI side-effects (minimap blip, accept
 * prompt, post-accept transition).
 */
export class RescueService {
  private state: RescueState = 'idle';
  private tug: TugShip | null = null;
  private detachTug: (() => void) | null = null;
  private readonly arrivalTarget = new THREE.Vector3();
  private readonly cruiseSpeed: number;
  private readonly arrivalStandoff: number;
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpPlayer = new THREE.Vector3();

  constructor(
    private readonly deps: RescueServiceDependencies,
    options: RescueServiceOptions = {}
  ) {
    this.cruiseSpeed = options.cruiseSpeed ?? DEFAULT_CRUISE_SPEED;
    this.arrivalStandoff = options.arrivalStandoff ?? DEFAULT_ARRIVAL_STANDOFF;
  }

  getState(): RescueState {
    return this.state;
  }

  getTug(): TugShip | null {
    return this.tug;
  }

  getTugPosition(): THREE.Vector3 | null {
    return this.tug?.object3D.position.clone() ?? null;
  }

  /** True when the player is allowed to interact with the parked tug. */
  isArrived(): boolean {
    return this.state === 'arrived';
  }

  /** Estimated remaining seconds until the tug parks in front of the
   *  player. Returns null when no rescue is in flight (state idle /
   *  arrived / returning). The estimate uses the live straight-line
   *  distance to the parking spot divided by cruise speed — it ignores
   *  the deceleration window, so the displayed ETA is intentionally
   *  generous (a couple of seconds slack at the end). */
  getEstimatedSeconds(): number | null {
    if (this.state !== 'en-route' || !this.tug) return null;
    const player = this.deps.getPlayerPosition();
    if (!player) return null;
    this.tmpPlayer.copy(player);
    const dir = this.tmpDir
      .copy(this.tmpPlayer)
      .sub(this.tug.object3D.position);
    const distToPlayer = dir.length();
    if (distToPlayer < 0.001) return 0;
    const remaining = Math.max(0, distToPlayer - this.arrivalStandoff);
    return remaining / this.cruiseSpeed;
  }

  /** Spawn the tug at the station and head for the player. The caller
   *  is responsible for gating this on the actual rescue conditions
   *  (fuel = 0, not already in flight, etc.) — this method just runs
   *  the simulation. */
  request(): void {
    if (this.state !== 'idle') return;
    const player = this.deps.getPlayerPosition();
    if (!player) return;

    const station = this.deps.getStationPosition();

    this.tug = new TugShip();
    this.tug.object3D.position.copy(station);
    // Spawn just outside the station hull so the silhouette reads as
    // "departing" rather than "phasing through the dock".
    const departDir = this.tmpDir.copy(player).sub(station);
    if (departDir.lengthSq() < 0.01) departDir.set(0, 0, -1);
    departDir.normalize();
    this.tug.object3D.position.addScaledVector(departDir, 60);

    this.detachTug = this.deps.attachToScene(this.tug);
    this.state = 'en-route';
  }

  /** Player accepted the rescue at the parked tug. The actual
   *  back-to-station transition is handled by the GameDirector (it owns
   *  the camera/scene fade); this method just flips the state so the
   *  director can pick it up and call `complete()` after the fade. */
  accept(): void {
    if (this.state !== 'arrived') return;
    this.state = 'returning';
  }

  /** Called by the GameDirector once the player has been teleported
   *  back to the station — frees the tug and resets the state. */
  complete(): void {
    if (this.tug) {
      this.detachTug?.();
      this.detachTug = null;
      this.tug = null;
    }
    this.state = 'idle';
  }

  /** Force-cancel an in-progress rescue. Used when the player dies, the
   *  scene transitions away from space, etc. */
  cancel(): void {
    if (this.state === 'idle') return;
    if (this.tug) {
      this.detachTug?.();
      this.detachTug = null;
      this.tug = null;
    }
    this.state = 'idle';
  }

  /** Drive the tug toward the player every frame while en-route. */
  update(dt: number): void {
    if (!this.tug) return;
    if (this.state === 'returning' || this.state === 'idle') return;

    this.tug.update(dt);

    const playerPos = this.deps.getPlayerPosition();
    if (!playerPos) return;
    this.tmpPlayer.copy(playerPos);

    if (this.state === 'en-route') {
      // Aim for a parking spot a fixed distance away from the player on
      // the line connecting the tug to the player. As the player moves
      // (slowly, since they're out of fuel) the target keeps tracking,
      // so the tug always parks in front of them.
      const dir = this.tmpDir.copy(this.tmpPlayer).sub(this.tug.object3D.position);
      const distToPlayer = dir.length();
      if (distToPlayer < 0.001) return;
      dir.divideScalar(distToPlayer);

      this.arrivalTarget
        .copy(this.tmpPlayer)
        .addScaledVector(dir, -this.arrivalStandoff);

      const toTarget = this.tmpDir
        .copy(this.arrivalTarget)
        .sub(this.tug.object3D.position);
      const remaining = toTarget.length();

      // Approach + decelerate as we close in on the parking spot so the
      // tug doesn't overshoot.
      const approachWindow = 80;
      const speedFactor = Math.min(1, remaining / approachWindow);
      const speed = this.cruiseSpeed * Math.max(0.18, speedFactor);

      if (remaining < 1.5) {
        // Snap to target and switch to arrived. The player can still
        // drift; arrived-state tracking will keep the tug parked.
        this.tug.object3D.position.copy(this.arrivalTarget);
        this.tug.velocity.set(0, 0, 0);
        this.tug.setThrust(0.15);
        this.state = 'arrived';
        // Snap heading so the tug faces the player when parked.
        const facing = this.tmpDir
          .copy(this.tmpPlayer)
          .sub(this.tug.object3D.position)
          .normalize();
        this.tug.object3D.rotation.y = Math.atan2(facing.x, facing.z) + Math.PI;
        return;
      }

      toTarget.divideScalar(remaining);
      this.tug.velocity.copy(toTarget).multiplyScalar(speed);
      this.tug.object3D.position.addScaledVector(this.tug.velocity, dt);
      this.tug.setThrust(speedFactor);
      this.tug.faceVelocity(dt);
      return;
    }

    if (this.state === 'arrived') {
      // Soft station-keeping: keep the tug pointed at the player and
      // gently drift to the parking spot if the player has moved.
      const dir = this.tmpDir.copy(this.tmpPlayer).sub(this.tug.object3D.position);
      const distToPlayer = dir.length();
      if (distToPlayer < 0.001) return;
      dir.divideScalar(distToPlayer);

      this.arrivalTarget
        .copy(this.tmpPlayer)
        .addScaledVector(dir, -this.arrivalStandoff);

      const toTarget = this.tmpDir
        .copy(this.arrivalTarget)
        .sub(this.tug.object3D.position);
      const remaining = toTarget.length();
      if (remaining > 1.0) {
        toTarget.divideScalar(remaining);
        const driftSpeed = Math.min(this.cruiseSpeed * 0.18, remaining * 1.5);
        this.tug.velocity.copy(toTarget).multiplyScalar(driftSpeed);
        this.tug.object3D.position.addScaledVector(this.tug.velocity, dt);
        this.tug.setThrust(0.2);
      } else {
        this.tug.velocity.set(0, 0, 0);
        this.tug.setThrust(0.1);
      }

      // Always face the player while parked.
      const facing = this.tmpDir
        .copy(this.tmpPlayer)
        .sub(this.tug.object3D.position)
        .normalize();
      const targetYaw = Math.atan2(facing.x, facing.z) + Math.PI;
      const cur = this.tug.object3D.rotation.y;
      let diff = targetYaw - cur;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.tug.object3D.rotation.y = cur + diff * Math.min(1, dt * 2.5);
    }
  }
}
